import { useFormik } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Select, { SingleValue } from 'react-select'
import { useReactToPrint } from 'react-to-print'
import { Button, Card, CardBody, Col, Container, Form, UncontrolledTooltip } from 'reactstrap'
import * as Yup from 'yup'
import { AgentOption, formatAgentOptions } from '../../../../api/agents/formatters'
import agents from '../../../../api/agents/hooks'
import auth from '../../../../api/auth/hooks'
import inDepthAnalysis from '../../../../api/inDepthAnalysis/hooks'
import { DepthAnalysisQuestions, DepthAnalysisSections, FormSubmission, FormSubmissionAnswers } from '../../../../api/inDepthAnalysis/types'
import FormActionButtons from '../../../Common/FormActionButtons'
import PageTitle from '../../../Common/PageTitle'
import BadgeRiskAssessment from '../../BadgeRiskAssessment/BadgeRiskAssessment'
import DynamicUI from '../../DynamicUI/DynamicUI'
import { convertEndpointDataInFormikData, findConnectedQuestionsIdsToDelete, mapContactDataToFormSubmissionAnswers, parseNumber } from '../../helpers'
import { useCalculateRisk } from '../../hooks'
import InDepthAnalysisPrintForm from '../../InDepthAnalysisPrintForm/InDepthAnalysisPrintForm'
import { ValidationObject } from '../../types'
import { createValidationSchema } from '../validationSchema'
import { InDepthAnalysisCreateEditRenderFormProps } from './InDepthAnalysisCreateEditRenderForm.types'

const InDepthAnalysisCreateEditRenderForm = (props: InDepthAnalysisCreateEditRenderFormProps) => {
    const { sections, isEdit, formSubmisson, contact, id, setSections, formSubmissionid, form } = props
    const history = useHistory()
    const sectionRefs = useRef<HTMLDivElement[]>([])
    const selectRefs = useRef<HTMLDivElement[]>([])
    const printContentRef = useRef<HTMLDivElement>(null)
    const queryParams = new URLSearchParams(location.search)
    const formId = queryParams.get('formId') as string

    const [connectedQuestionsIdsToDelete] = useState(findConnectedQuestionsIdsToDelete(form))

    const { data: agentsData } = agents.GetAgents.useQuery()
    const { data: currentAgentId } = auth.CurrentAgent.useQuery({ select: (data) => data.id })
    const { mutate: createUpdateMutate } = inDepthAnalysis.CreateUpdateFormsSubmission.useMutation(id, formSubmissionid, isEdit ? true : false)

    const agentOptions = formatAgentOptions(agentsData)

    let defaultAgent
    let initialValues

    if (isEdit) {
        defaultAgent = agentOptions.find((option) => option.agentId === formSubmisson?.agent_id)
        initialValues = convertEndpointDataInFormikData(formSubmisson?.answers) ?? {}
    } else {
        defaultAgent = agentOptions.find((option) => option.agentId === currentAgentId)
        const contactAnswers = mapContactDataToFormSubmissionAnswers(contact)
        initialValues = convertEndpointDataInFormikData(contactAnswers) ?? {}
    }

    const [selectedAgent, setSelectedAgent] = useState<AgentOption | undefined>(undefined)
    const selectedAgentOption = selectedAgent ?? defaultAgent

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: Yup.object(createValidationSchema(sections)),
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: (values: ValidationObject) => {
            const answers: FormSubmissionAnswers[] = Object.entries(values).map(([key, value]) => {
                // key example: question_3_string_NONE
                const [, questionId, type, riskFactor] = key.split('_')

                switch (type) {
                    case 'boolean':
                        return {
                            question_id: Number(questionId),
                            selectedOption: value as string | number,
                            type,
                            riskFactor,
                        }
                    case 'string':
                        return {
                            question_id: Number(questionId),
                            stringValue: value as string,
                            type,
                            riskFactor,
                        }
                    case 'text':
                        return {
                            question_id: Number(questionId),
                            textValue: value as string,
                            type,
                            riskFactor,
                        }
                    case 'select':
                        return {
                            question_id: Number(questionId),
                            selectedOption: value as string | number,
                            type,
                            riskFactor,
                        }
                    case 'date':
                        return {
                            question_id: Number(questionId),
                            dateValue: value as string,
                            type,
                            riskFactor,
                        }
                    case 'float':
                        return {
                            question_id: Number(questionId),
                            floatValue: value ? Number(parseNumber(value.toString())) : undefined,
                            type,
                            riskFactor,
                        }
                    default:
                        throw new Error(`Unexpected type: ${type}`)
                }
            })

            const submitValues: FormSubmission = {
                form_id: formId,
                contact_id: id,
                riskAssessment: riskAssessment.risk,
                agent_id: selectedAgentOption?.agentId as number,
                answers,
            }

            createUpdateMutate(submitValues)
        },
    })

    useEffect(() => {
        for (const key in formik.values) {
            if (Object.hasOwn(formik.errors, key)) {
                delete formik.errors[key]
            }
            if (formik.values[key] === '') {
                delete formik.values[key]
            }
        }
    }, [formik.errors, formik.values])

    const { riskAssessment } = useCalculateRisk(formik.values ?? {}, sections)

    const updateRequiredBySection = (sectionId: number, newValue: boolean) => {
        setSections((prevSections: DepthAnalysisSections[]) =>
            prevSections?.map((section: { id: number; questions: DepthAnalysisQuestions[] }) =>
                section.id === sectionId
                    ? {
                          ...section,
                          questions: section.questions.map((question: DepthAnalysisQuestions) => ({
                              ...question,
                              required: newValue,
                          })),
                      }
                    : section
            )
        )
    }

    const updateRequiredByQuestion = (sectionId: number, questionId?: number, newValue?: boolean) => {
        setSections((prevSections: DepthAnalysisSections[]) =>
            prevSections?.map((section: { id: number; questions: DepthAnalysisQuestions[] }) =>
                section.id === sectionId
                    ? {
                          ...section,
                          questions: section.questions.map((question: DepthAnalysisQuestions) => {
                              const condition =
                                  question.id === questionId
                                      ? {
                                            ...question,
                                            required: newValue ?? false,
                                        }
                                      : {
                                            ...question,
                                        }
                              return condition
                          }),
                      }
                    : section
            )
        )
    }

    const renderSectionsAndQuestions = sections
        ?.sort((a, b) => (a?.priority ?? 0) - (b?.priority ?? 0))
        ?.flatMap((section: DepthAnalysisSections, sectionIndex: number) => {
            return [
                <div
                    key={`section_${section.id}`}
                    ref={(el) => {
                        if (el) sectionRefs.current[section.id] = el
                    }}
                    style={{
                        display: `${section.hidden ? 'none' : 'block'}`,
                    }}
                    id={`section_${section.id}`}
                >
                    <h3 className="pt-4" key={`category-${sectionIndex}`}>
                        {section.title}
                    </h3>
                    <div className="d-flex" key={`list-${sectionIndex}`} style={{ flexWrap: 'wrap' }}>
                        {section.questions.map((question: DepthAnalysisQuestions, questionIndex: number) => {
                            return (
                                <DynamicUI
                                    sectionRefs={sectionRefs}
                                    selectRefs={selectRefs}
                                    key={`item-${sectionIndex}-${questionIndex}`}
                                    question={question}
                                    questionIndex={questionIndex}
                                    errors={formik.errors}
                                    formik={formik}
                                    questions={section.questions}
                                    connectedQuestionsIdsToDelete={connectedQuestionsIdsToDelete}
                                    updateRequiredBySection={updateRequiredBySection}
                                    updateRequiredByQuestion={updateRequiredByQuestion}
                                />
                            )
                        })}
                    </div>
                </div>,
            ]
        })

    const renderFormButtons = () => (
        <FormActionButtons
            disabled={false}
            hasCancel={true}
            cancelAction={() =>
                history.push({
                    pathname: contact?.contactType === 'person' ? `/contacts/${id}` : `/companies/${id}`,
                    state: { activeTab: 'inDepthAnalysis' },
                })
            }
        />
    )

    const handlePrint = useReactToPrint({
        contentRef: printContentRef,
        pageStyle: `
            @page {
                margin: 10px;
            }
            @media print {
                .page-break-before { page-break-before: always; }
                .page-break-after { page-break-after: always; }
            }
        `,
    })

    return (
        <div className="page-content">
            <Container fluid>
                <Col>
                    <div className="page-title-box ps-0 d-sm-flex align-items-start mt-3">
                        <div>
                            <PageTitle className="px-2" title={isEdit ? `Uredi dubinsku analizu` : `Nova dubinska analiza`} />
                            <Col sm="12" className="d-flex align-items-center">
                                {contact?.contactType === 'person' ? (
                                    <div className="px-2 d-flex align-items-center">
                                        <span className="fw-medium me-2">Kontakt: </span>
                                        {contact?.contactPerson?.firstName} {contact?.contactPerson?.lastName}
                                    </div>
                                ) : (
                                    <div className="px-2 d-flex align-items-center">
                                        <span className="fw-medium me-2">Poduzeće: </span>
                                        {contact?.contactCompany?.name}
                                    </div>
                                )}

                                <div className="px-2 d-flex align-items-center">
                                    <span className="fw-medium me-2">Ocjena rizičnosti: </span>
                                    <BadgeRiskAssessment riskAssessment={riskAssessment.risk} />
                                </div>
                            </Col>
                        </div>
                    </div>
                    <Form className="my-3" id="inDepthAnalysis" onSubmit={(e) => formik.handleSubmit(e)}>
                        <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center me-2">
                                <Button
                                    type="button"
                                    id="print-form"
                                    color="send"
                                    className="btn-label"
                                    onClick={() => {
                                        handlePrint()
                                    }}
                                >
                                    <i className="mdi mdi-printer-outline align-bottom label-icon align-middle fs-16 me-2"></i>
                                    Ispiši
                                </Button>
                                <UncontrolledTooltip placement="right" target="print-form">
                                    Ispisuje prazan formular
                                </UncontrolledTooltip>
                            </div>
                            {renderFormButtons()}
                        </div>
                        <Card className="my-3">
                            <CardBody className="p-4">
                                <div>
                                    <h3 className="pt-4">AGENT</h3>
                                    <div className="d-flex" style={{ flexWrap: 'wrap' }}>
                                        <div className="d-flex align-items-end justify-content-between p-3" style={{ flexBasis: '100%' }}>
                                            <div className="d-flex" style={{ flexBasis: '50%' }}>
                                                <Select
                                                    className="w-100"
                                                    onChange={(e: SingleValue<AgentOption>) => {
                                                        setSelectedAgent(e ?? undefined)
                                                    }}
                                                    options={agentOptions}
                                                    value={selectedAgentOption ?? null}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {renderSectionsAndQuestions}
                            </CardBody>
                        </Card>
                        {renderFormButtons()}
                    </Form>
                </Col>

                {/* hidden print content */}
                <div style={{ display: 'none' }}>
                    <InDepthAnalysisPrintForm ref={printContentRef} sections={form?.sections || []} contact={contact} formTitle={form?.title} />
                </div>
            </Container>
        </div>
    )
}

export default InDepthAnalysisCreateEditRenderForm
